import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { splitLang, langsFor, langPath } from './MainPageTranslator';

const SEO = ({ title, description, keywords, canonical, type = 'website', schema, image, speakable, noindex, noscriptContent, dateModified, noHreflang }) => {
  const baseUrl = 'https://brunchi.shop';
  const fullTitle = title || 'Brunchi.shop | Wine, Beer & Spirits | Playa Blanca';
  const { lang, base } = splitLang(useLocation().pathname);
  const isMain = langsFor(base).length > 0;
  const fullCanonical = isMain && lang !== 'en' ? `${baseUrl}${langPath(lang, base)}` : (canonical ? `${baseUrl}${canonical}` : baseUrl);
  const mainAlternates = langsFor(base).map(l => [l, `${baseUrl}${langPath(l, base)}`]);
  const ogImage = image || 'https://customer-assets.emergentagent.com/job_brunchi-local/artifacts/vbzvxphp_IMG_8009-scaled.jpeg';

  const speakableCss = speakable || [".speakable-answer", "h1", "h2", ".description"];

  const aiAssistantSchema = {
    "@context": "https://schema.org",
    "@type": "AIAssistantAction",
    "name": "Brunchi.shop AI Knowledge",
    "description": "Wine, spirits, craft beer shop and terrace bar at Marina Rubicon, Playa Blanca, Lanzarote",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${baseUrl}/llms-full.txt`
    },
    "result": {
      "@type": "Thing",
      "name": "Brunchi.shop product and service information"
    }
  };

  return (
    <>
    <Helmet {...(isMain ? { htmlAttributes: { lang } } : {})}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* hreflang for multilingual tourists */}
      {!noHreflang && <link rel="alternate" hreflang="en" href={isMain ? `${baseUrl}${base === '/' ? '' : base}` : fullCanonical} />}
      {!noHreflang && <link rel="alternate" hreflang="x-default" href={isMain ? `${baseUrl}${base === '/' ? '' : base}` : fullCanonical} />}
      {!noHreflang && mainAlternates.map(([l, href]) => <link key={l} rel="alternate" hreflang={l} href={href} />)}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Brunchi.shop" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:image" content={ogImage} />
      {!image && <meta property="og:image:width" content="1200" />}
      {!image && <meta property="og:image:height" content="630" />}
      <meta property="og:image:alt" content={description} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Attribution */}
      <meta name="author" content="Brunchi.shop" />
      <meta name="copyright" content="Brunchi.shop" />

      {/* JSON-LD Schemas */}
      {schema && Array.isArray(schema) ? (
        schema.map((s, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
        ))
      ) : schema ? (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      ) : null}

      {/* Speakable Schema — GLOBAL on every page for voice search */}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": fullTitle,
        "url": fullCanonical,
        "datePublished": "2025-06-01",
        "dateModified": dateModified || "2026-02-15",
        "lastReviewed": dateModified || "2026-02-15",
        "isPartOf": {"@type": "WebSite", "name": "Brunchi.shop", "url": "https://brunchi.shop"},
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": speakableCss
        }
      })}</script>

      {/* AI Assistant Schema — helps AI search engines cite us */}
      <script type="application/ld+json">{JSON.stringify(aiAssistantSchema)}</script>
    </Helmet>
    {noscriptContent && (
      <noscript>
        <div>
          <h1>{fullTitle}</h1>
          <p>{description}</p>
          <div dangerouslySetInnerHTML={{ __html: noscriptContent }} />
        </div>
      </noscript>
    )}
    </>
  );
};

export default SEO;
